import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileSignature, Download, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function InkReports() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/ink/reports").then(({ data }) => {
      setItems(data);
      if (data.length) setSelected(data[0]);
    }).catch(() => {});
  }, []);

  const copy = () => {
    if (!selected) return;
    navigator.clipboard.writeText(selected.report_markdown).then(() => toast.success("Copied"));
  };

  const download = () => {
    if (!selected) return;
    const blob = new Blob([selected.report_markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const cust = selected.customer_name.replace(/\s+/g, "-").toLowerCase();
    a.href = url; a.download = `${cust}-analysis.md`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
          <Link to="/app/ink" className="hover:text-primary">Ink &amp; Print</Link> / Saved reports
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="reports-title">
          Saved Customer Reports
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Every Copilot analysis you save shows up here.</p>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
          <FileSignature className="w-8 h-8 mx-auto text-muted-foreground" />
          <div className="font-display text-lg mt-4">No saved reports yet</div>
          <p className="text-sm text-muted-foreground mt-2">
            Run an analysis in the <Link to="/app/ink/copilot" className="text-primary hover:underline">Copilot</Link> and hit save.
          </p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <div className="border border-border bg-card rounded-sm max-h-[calc(100vh-14rem)] overflow-y-auto">
              {items.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setSelected(r)}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 transition-colors ${
                    selected?.id === r.id ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-secondary/50"
                  }`}
                  data-testid={`report-${r.id}`}
                >
                  <div className="font-medium text-sm truncate">{r.customer_name}</div>
                  <div className="text-[10px] font-mono-plex uppercase tracking-widest text-muted-foreground mt-1">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div className="lg:col-span-3">
            {selected && (
              <div className="border border-border bg-card rounded-sm">
                <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                  <div className="font-display font-bold">{selected.customer_name}</div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={copy} className="h-8 rounded-sm text-xs" data-testid="copy-report-btn">
                      <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                    </Button>
                    <Button size="sm" variant="ghost" onClick={download} className="h-8 rounded-sm text-xs" data-testid="download-report-btn">
                      <Download className="w-3.5 h-3.5 mr-1.5" /> .md
                    </Button>
                  </div>
                </div>
                <div className="p-6 lg:p-8 text-[14px] leading-relaxed whitespace-pre-wrap max-h-[calc(100vh-16rem)] overflow-y-auto">
                  {selected.report_markdown}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
